import { Badge } from '../../components/ui'
import type { CataloguePost } from '../../api/catalogue'
import { t } from '../../i18n'
import { PostForm } from './PostForm'

/**
 * The advert on the left, what we know about it on the right.
 *
 * Side by side because the decision this screen asks for is a comparison:
 * somebody reads the post, reads the fields, corrects what is wrong and creates
 * the object. Showing only the fields would ask them to trust a model that has
 * often read three words; showing only the post would ask them to type it twice.
 *
 * The photograph is on the left with the words because it is part of the advert
 * and often the more informative part — "Via Degli Ortaggi - 1100€" is four
 * pictures and a price, and the pictures are what say whether it is a studio or
 * somebody's spare room.
 */
export function PostCard({ post }: { readonly post: CataloguePost }) {
  return (
    <div className="catalogue__card">
      <section className="catalogue__source">
        <header className="catalogue__meta">
          <a href={post.link} target="_blank" rel="noreferrer" className="catalogue__link">
            {post.link.replace('https://', '')}
          </a>
          <span className="catalogue__when">
            {t('catalogue.published', { date: date(post.publishedAt) })}
            {post.editedAt ? `, ${t('catalogue.edited', { date: date(post.editedAt) })}` : ''}
          </span>
          <Badge tone={post.photos > 0 ? 'neutral' : 'warning'}>
            {post.photos > 0 ? t('catalogue.photos', { count: post.photos }) : t('catalogue.noPhotos')}
          </Badge>
        </header>

        <div className="catalogue__advert">
          {post.coverURL === undefined ? null : (
            <img
              className="catalogue__cover"
              src={post.coverURL}
              alt={t('catalogue.photoOf')}
              loading="lazy"
            />
          )}
          <pre className="catalogue__body">{post.body}</pre>
        </div>

        {post.note ? <p className="catalogue__note">{post.note}</p> : null}
      </section>

      <section className="catalogue__parsed">
        <h3 className="catalogue__parsedTitle">{t('catalogue.read')}</h3>
        <p className="catalogue__hint">{t('catalogue.readHint')}</p>
        <PostForm post={post} />
      </section>
    </div>
  )
}

function date(iso: string): string {
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleDateString('ru-RU')
}
